import React, { useState, useEffect } from "react";
import { Row, Col, Button, Table, Modal, Form } from "react-bootstrap";
import axios from "axios";
import { useParams } from "react-router-dom";
import DeleteDialog from "../../components/DeleteDialog";
import toast from "react-hot-toast";
import { Formik, Field } from "formik";
import * as Yup from "yup";

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Prevent leading spaces but allow spaces after first character
const preventSpaces = (e) => {
  if (
    e.key === ' ' &&
    (!e.target.value || /^\s*$/.test(e.target.value + e.key))
  ) {
    e.preventDefault();
  }
};

// Prevent pasting only spaces
const preventPasteSpaces = (e) => {
  if (/^\s*$/.test(e.clipboardData.getData('text'))) {
    e.preventDefault();
  }
};

const HospitalDetails = () => {
  const { hospitalId } = useParams();
  const [hospital, setHospital] = useState(null);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  useEffect(() => {
    const fetchHospitalDetails = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/superadmin/getHospitalById/${hospitalId}`
        );
        setHospital(response.data.hospital);
      } catch (error) {
        console.error("Error fetching hospital details:", error);
      }
    };

    fetchHospitalDetails();
  }, [hospitalId]);

  // Yup Validation Schema
  // const validationSchema = Yup.object().shape({
  //   name: Yup.string()
  //     .required("Admin name is required")
  //     .test(
  //       "no-empty-or-spaces",
  //       "Name cannot be empty or spaces only",
  //       (value) => value && value.trim().length > 0 && !/^\s+$/.test(value)
  //     ),
  //   email: Yup.string()
  //     .required("Email is required")
  //     .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format")
  //     .test(
  //       "no-empty-or-spaces",
  //       "Email cannot be empty or spaces only",
  //       (value) => value && value.trim().length > 0 && !/^\s+$/.test(value)
  //     ),
  //   password: Yup.string()
  //     .required("Password is required")
  //     .min(6, "Password must be at least 6 characters")
  //     .matches(/^\S*$/, "Password cannot contain spaces or tabs"),
  //   contactNumber: Yup.string()
  //     .required("Contact number is required")
  //     .matches(/^[0-9]{10}$/, 'Contact number must be exactly 10 digits'),
  // });

  const openEditAdminModal = (admin) => {
    setSelectedAdmin(admin);
    setShowAdminModal(true);
  };

  const handleEditAdmin = async (values) => {
    try {
      await axios.put(
        `${BASE_URL}/api/superadmin/updateAdmin/${values.id}`,
        values
      );
      toast.success("Admin updated successfully");

      setHospital((prev) => ({
        ...prev,
        admins: prev.admins.map((admin) =>
          admin.id === values.id ? { ...admin, ...values } : admin
        ),
      }));

      setShowAdminModal(false);
    } catch (error) {
      toast.error("Failed to update admin");
    }
  };

  return (
    <Row>
      <Col xs={12} className="px-4 py-3">
        {hospital && (
          <>
            <h3 className="global-heading mb-2">
              {hospital.name.charAt(0).toUpperCase() +
                hospital.name.slice(1).toLowerCase()}{" "}
              Details
            </h3>

            <p>
              <strong>Address: </strong> {hospital.address}
            </p>
            <p>
              <strong>Contact Number: </strong> {hospital.contact_info}
            </p>

            {hospital.admins?.length > 0 ? (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Sr.No.</th>
                    <th>Admin Name</th>
                    <th>Email</th>
                    <th className="text-center">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {hospital.admins.map((admin, index) => (
                    <tr key={admin.id}>
                      <td>{index + 1}</td>
                      <td>{admin.name}</td>
                      <td>{admin.email}</td>
                      <td className="text-center">
                        <Button
                          className="border-0 fw-semibold primary-class bg-transparent"
                          onClick={() => openEditAdminModal(admin)}
                        >
                          <span className="material-icons me-1" style={{ fontSize: 16 }}>
                            edit
                          </span>
                        </Button>

                        <DeleteDialog
                          id={admin.id}
                          path={`${BASE_URL}/api/superadmin/deleteAdmin/${admin.id}`}
                          message="Admin deleted successfully"
                          icon="button"
                          onSuccess={() =>
                            setHospital((prev) => ({
                              ...prev,
                              admins: prev.admins.filter((a) => a.id !== admin.id),
                            }))
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p>No admins found for this hospital.</p>
            )}
          </>
        )}
      </Col>

      {/* Edit Admin Modal */}
      <Modal
        show={showAdminModal}
        onHide={() => setShowAdminModal(false)}
        centered
        className="form-modal p-3"
      >
        <Formik
          enableReinitialize
          initialValues={{
            id: selectedAdmin?.id || "",
            name: selectedAdmin?.name || "",
            email: selectedAdmin?.email || "",
            password: "",
            hospitalName: hospital?.name || "",
            // contactNumber: hospital?.contact_info || "",
          }}
          // validationSchema={validationSchema}
          onSubmit={handleEditAdmin}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Modal.Header closeButton>
                <Modal.Title>Edit Admin</Modal.Title>
              </Modal.Header>

              <Modal.Body>

                {/* Admin Name */}
                <Form.Group className="mb-3">
                  <Form.Label>Admin Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.name && !!errors.name}
                    onKeyDown={preventSpaces}
                    onPaste={preventPasteSpaces}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.name}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Email */}
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && !!errors.email}
                    onKeyDown={(e) => {
                      if (e.key === " ") {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      const pasteData = e.clipboardData.getData("text");
                      if (/\s/.test(pasteData)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Password */}
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.password && !!errors.password}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Tab') {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      const pasteData = e.clipboardData.getData("text");
                      if (/\s/.test(pasteData)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.password}
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Hospital Name (Read-only) */}
                <Form.Group className="mb-3">
                  <Form.Label>Hospital Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="hospitalName"
                    value={values.hospitalName}
                    readOnly
                  />
                </Form.Group>

                {/* Contact Number */}
                {/* <Form.Group className="mb-3">
                  <Form.Label>Contact Number</Form.Label>
                  <Field
                    name="contactNumber"
                    as={Form.Control}
                    type="text"
                    maxLength={10}
                    isInvalid={touched.contactNumber && !!errors.contactNumber}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Tab") {
                        e.preventDefault();
                      }
                      if (
                        !/[0-9]/.test(e.key) &&
                        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onPaste={(e) => {
                      const pasteData = e.clipboardData.getData("text");
                      if (!/^\d*$/.test(pasteData)) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.contactNumber}
                  </Form.Control.Feedback>
                </Form.Group> */}
              </Modal.Body>

              <Modal.Footer>
                <Button type="submit" style={{ background: "#0A7884", border: "none" }}>
                  Save Changes
                </Button>
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </Modal>
    </Row>
  );
};

export default HospitalDetails;
